import React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Plus, RotateCcw, X } from 'lucide-react'

import type { HgConversation } from '../../api/client'

const SURFACE_SPRING = { type: 'spring' as const, stiffness: 500, damping: 30 }
const BUTTON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }

/** The store's timestamps are UTC without a zone marker. */
const parseUtc = (value: string): Date =>
    new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`)

const whenLabel = (value: string, now: Date = new Date()): string => {
    const date = parseUtc(value)
    if (Number.isNaN(date.getTime())) return ''
    if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    }
    const sameYear = date.getFullYear() === now.getFullYear()
    return date.toLocaleDateString([], sameYear
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' })
}

export const chatTitle = (conversation: HgConversation | null | undefined): string =>
    conversation?.title?.trim() || 'New chat'

interface ConversationListProps {
    conversations: HgConversation[]
    activeId: number | null
    showArchived: boolean
    onToggleArchived: () => void
    onSelect: (id: number) => void
    onNew: () => void
    onArchive: (id: number) => void
    onRestore: (id: number) => void
}

const ConversationList: React.FC<ConversationListProps> = ({
    conversations,
    activeId,
    showArchived,
    onToggleArchived,
    onSelect,
    onNew,
    onArchive,
    onRestore,
}) => (
    <nav className="flex flex-col gap-2" aria-label="Chats" data-testid="chat-list">
        <motion.button
            type="button"
            onClick={onNew}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            transition={BUTTON_SPRING}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-action hover:bg-action-dark px-3 py-2 text-sm font-medium text-plum cursor-pointer"
        >
            <Plus className="w-4 h-4" />
            New chat
        </motion.button>

        <ul className="flex flex-col gap-0.5">
            <AnimatePresence initial={false}>
                {conversations.map((c) => {
                    const active = c.id === activeId
                    return (
                        <motion.li
                            key={c.id}
                            layout
                            initial={{ opacity: 0, scale: 0.95, y: -8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={SURFACE_SPRING}
                            style={{ transformOrigin: 'top center' }}
                            className={`group flex items-center gap-1 rounded-lg ${
                                active ? 'bg-white/80' : 'hover:bg-white/50'
                            }`}
                        >
                            <button
                                type="button"
                                onClick={() => onSelect(c.id)}
                                aria-current={active ? 'page' : undefined}
                                className="flex-1 min-w-0 text-left px-3 py-2 cursor-pointer"
                            >
                                <div className={`truncate text-sm ${active ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                    {chatTitle(c)}
                                </div>
                                <div className="text-xs text-gray-500">{whenLabel(c.last_activity_at)}</div>
                            </button>
                            {showArchived ? (
                                <motion.button
                                    type="button"
                                    onClick={() => onRestore(c.id)}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={BUTTON_SPRING}
                                    title="Restore"
                                    aria-label="Restore"
                                    className="mr-2 p-1 text-gray-400 hover:text-gray-700 cursor-pointer"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </motion.button>
                            ) : (
                                <motion.button
                                    type="button"
                                    onClick={() => onArchive(c.id)}
                                    whileHover={{ scale: 1.1, rotate: 90 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={BUTTON_SPRING}
                                    title="Archive"
                                    aria-label="Archive"
                                    className="mr-2 p-1 text-gray-400 hover:text-gray-700 cursor-pointer sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                                >
                                    <X className="w-4 h-4" />
                                </motion.button>
                            )}
                        </motion.li>
                    )
                })}
            </AnimatePresence>
        </ul>
        {conversations.length === 0 && (
            <p className="px-3 text-sm text-gray-500">{showArchived ? 'Nothing archived.' : 'No chats yet.'}</p>
        )}

        <button
            type="button"
            onClick={onToggleArchived}
            className="self-start px-3 text-xs text-gray-500 hover:text-gray-800 cursor-pointer"
        >
            {showArchived ? 'Back to chats' : 'Archived'}
        </button>
    </nav>
)

export default ConversationList
